import { selectWarehouses } from 'containers/Warehouses/warehousesSlice'
import { useEffect, useState } from 'react'
import type { SelectedPeriods } from '..'
import { useReduxSelector } from 'store'
import { POST } from 'rest/request'
import * as urls from 'rest/urls'

const usePaymentData = () => {
  const [isLoading, setLoading] = useState(false)
  const warehouses = useReduxSelector(selectWarehouses)
  const [data, setData] = useState<payments.CancelRentalResponse | null>(null)

  const generateBody = (warehouses: warehouse.Warehouse[]): payments.CancelRentalBody => {
    const query = new URLSearchParams(window.location.search)
    const warehouseIdFromQuery = query.get('warehouseId')
    if (warehouseIdFromQuery) {
      const warehouseFromQuery = warehouses.find(({ objectId }) => objectId === warehouseIdFromQuery)
      if (warehouseFromQuery) {
        return {
          action: 'payment',
          decision: 'payNow',
          objects: [{ id: warehouseFromQuery.objectId, object: 'warehouse' }]
        }
      }
    }
    
    const objects = warehouses.reduce((r, { objectId, isShared, permissions }) => {
      if (!isShared || permissions?.includes('payment')) {
        r.push({ id: objectId, object: 'warehouse' })
      }
      return r
    }, [] as payments.CancelRentalBodyObject[])
    return {
      action: 'payment',
      decision: 'payNow',
      objects
    }
  }

  const getPaymentsData = async (warehouses: warehouse.Warehouse[]) => {
    setLoading(true)
    try {
      const body = generateBody(warehouses)
      const response: payments.CancelRentalResponse = await POST(urls.warehousesAction, { body })
      setLoading(false)
      if (response.warehouses) {
        const warehousesObject = warehouses.reduce((r, warehouse) => {
          r[warehouse.objectId] = warehouse
          return r
        }, {} as Record<string, warehouse.Warehouse>)
        setData({
          ...response,
          warehouses: response.warehouses?.map(warehouse => ({
            ...warehouse,
            placeAddress: warehousesObject[warehouse.id]?.placeShortAddress,
            schedule: warehouse.schedule.map(schedule => ({
              ...schedule,
              tiles: schedule.tiles.filter(tile => Number(tile.grossPrice) > 0)
            }))
          }))
        })
      }

    } catch (error) {
      setLoading(false)
    }
  }

  const generateInvoice = async (payments: payments.Payment[], selectedPeriods: SelectedPeriods, type?: string) => {
    if (!type || !data?.warehouses) return
    setLoading(true)
    const newDocument: payments.CancelRentalBodyNewDocument = {
      type,
      positions: data?.warehouses?.reduce((result, { id, object, schedule }) => {
        if (selectedPeriods[id] > -1) {
          result.push({
            id,
            object,
            date: 'now',
            warehouseRentingIds: getWarehouseRentingIds(selectedPeriods[id], schedule)
          })
        }
        return result
      }, [] as payments.CancelRentalBodyObject[]),
      payments
    }

    if (data.newDocuments) {
      newDocument.previousId = data.newDocuments.map(({ id }) => id)
    }

    const body: payments.CancelRentalBody = {
      ...generateBody(warehouses),
      newDocument
    }

    try {
      const response: payments.CancelRentalResponse = await POST(urls.warehousesAction, { body })
      if (response.newDocuments) {
        setLoading(false)
        setData(prev => ({ ...prev, ...response }))
        return response
      } else {
        throw new Error('Bad API 😡')
      }
    } catch (error) {
      setLoading(false)
      console.error(error)
    }
  }

  useEffect(() => {
    if (!warehouses || warehouses.length === 0) return
    getPaymentsData(warehouses)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warehouses])

  return {
    data,
    isLoading,
    generateInvoice
  }
}

const getWarehouseRentingIds = (selectedTimePeriod: number, schedule: payments.WarehouseRentalSchedule[]) => {
  let warehouseRentingIds: string[] = []
  if (schedule) {
    for (let i = 0; i <= selectedTimePeriod; i++) {
      warehouseRentingIds.push(...schedule[0].tiles[i].warehouseRentingIds)
    }
  }
  return warehouseRentingIds
}

export default usePaymentData